import { useState } from "react";
import axios from "axios";
import { Airplane, X, Pause, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (d) =>
  d
    ? d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Selecciona una fecha";

const formatLong = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const VacationControl = ({ paused, vacationUntil, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleStart = async () => {
    if (!date) {
      toast.error("Selecciona una fecha de regreso");
      return;
    }
    setSubmitting(true);
    try {
      const until = new Date(date);
      until.setHours(23, 59, 0, 0);
      await axios.post(
        `${API}/vacation`,
        { until: until.toISOString() },
        { withCredentials: true }
      );
      toast.success("Modo vacaciones activado", {
        description: `Pausado hasta ${formatLong(until.toISOString())}`,
      });
      setOpen(false);
      setDate(null);
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo activar";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnd = async () => {
    if (!confirm("¿Finalizar el modo vacaciones y reiniciar el temporizador?")) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/vacation`, { withCredentials: true });
      toast.success("Modo vacaciones finalizado", {
        description: "Temporizador reiniciado a 48 horas.",
      });
      onChanged?.();
    } catch {
      toast.error("Error al finalizar el modo vacaciones");
    } finally {
      setSubmitting(false);
    }
  };

  // Active vacation banner
  if (paused && vacationUntil) {
    return (
      <div
        className="bg-warn/10 border border-warn/40 rounded-xl p-5 md:p-6 mb-10 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="vacation-banner"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-full bg-warn/15 border border-warn/40 flex items-center justify-center text-warn shrink-0">
            <Airplane size={24} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Pause size={14} weight="fill" className="text-warn" />
              <span className="font-heading font-bold text-warn text-sm uppercase tracking-[0.2em]">
                Modo vacaciones activo
              </span>
            </div>
            <div className="text-white/80 text-sm">
              Pausado hasta{" "}
              <span className="mono font-mono text-white">
                {formatLong(vacationUntil)}
              </span>
            </div>
            <div className="text-xs text-white/50 mt-1">
              No se enviarán alertas durante este período.
            </div>
          </div>
        </div>
        <Button
          onClick={handleEnd}
          disabled={submitting}
          variant="outline"
          className="border-warn/50 text-warn hover:bg-warn/10 hover:text-warn shrink-0"
          data-testid="vacation-end-btn"
        >
          <Play size={14} weight="bold" className="mr-1.5" />
          Finalizar ahora
        </Button>
      </div>
    );
  }

  // Trigger button (no active vacation)
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <Button
          onClick={() => setOpen(true)}
          variant="outline"
          className="border-border hover:border-aqua/50 text-white/80 hover:text-white"
          data-testid="vacation-open-btn"
        >
          <Airplane size={16} weight="duotone" className="mr-2 text-aqua" />
          Modo vacaciones
        </Button>

        <DialogContent
          className="bg-surface border border-border max-w-md sm:max-w-[420px] text-white"
          data-testid="vacation-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight text-2xl flex items-center gap-2">
              <Airplane size={22} weight="duotone" className="text-aqua" />
              Modo vacaciones
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Pausa el temporizador hasta una fecha futura. Mientras esté activo, no se
              enviarán alertas de emergencia.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <label className="text-xs uppercase tracking-[0.25em] text-white/55 mb-2 block">
              Fecha de regreso
            </label>

            <div
              className="bg-background border border-border rounded-lg p-2 flex justify-center"
              data-testid="vacation-calendar-wrapper"
            >
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return d < today;
                }}
                initialFocus
                locale={es}
                className="text-white"
                classNames={{
                  caption_label: "text-sm font-bold text-white tracking-wide",
                  nav_button:
                    "h-7 w-7 bg-transparent text-white/70 hover:text-aqua hover:bg-aqua/10 rounded-md inline-flex items-center justify-center transition-colors",
                  head_cell:
                    "text-white/45 rounded-md w-9 font-medium text-[0.7rem] uppercase tracking-wider",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                  day: "h-9 w-9 p-0 font-normal text-white hover:bg-aqua/10 hover:text-aqua rounded-md transition-colors inline-flex items-center justify-center",
                  day_selected:
                    "bg-aqua text-background hover:bg-aqua hover:text-background focus:bg-aqua focus:text-background font-semibold",
                  day_today: "border border-aqua/50 text-aqua",
                  day_outside: "text-white/25",
                  day_disabled: "text-white/20 hover:bg-transparent hover:text-white/20 cursor-not-allowed",
                }}
              />
            </div>

            <div className="mt-3 text-xs text-white/55 text-center">
              {date ? (
                <>
                  Seleccionaste{" "}
                  <span className="mono font-mono text-aqua">{formatDate(date)}</span>
                </>
              ) : (
                <span className="text-white/40">Selecciona una fecha en el calendario</span>
              )}
            </div>

            <div className="mt-4 p-3 bg-background border border-border/60 rounded-lg text-xs text-white/55 leading-relaxed">
              El temporizador se reanudará automáticamente desde 48 horas a partir de la
              fecha seleccionada (al final del día).
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setDate(null);
              }}
              className="border-border text-white/70"
              data-testid="vacation-cancel-btn"
            >
              <X size={14} className="mr-1.5" />
              Cancelar
            </Button>
            <Button
              onClick={handleStart}
              disabled={submitting || !date}
              className="bg-aqua text-background hover:bg-aqua/90"
              data-testid="vacation-confirm-btn"
            >
              <Pause size={14} weight="bold" className="mr-1.5" />
              {submitting ? "Activando..." : "Activar pausa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VacationControl;
